import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, MapPin, Tags, AlertTriangle, Upload, Video, ImageIcon, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { api, thumbUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MediaTile } from "@/components/MediaTile";
import { cn } from "@/lib/utils";

// Owner work center: live canonical counts (same queries as the workflows) — shows queues, never tags/changes anything.
export const WorkQueue = ({ onUpload, onOpenMedia, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null); // "needs_tagging" | "unregistered"
  const [needsTag, setNeedsTag] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get("/admin/work-queues")).data); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (panel !== "needs_tagging") return;
    api.get("/admin/needs-tagging").then((r) => setNeedsTag(r.data)).catch(() => setNeedsTag({ count: 0, items: [] }));
  }, [panel, refreshKey]);
  if (!data) return null;
  const q = data.queues;
  const cards = [
    { key: "inbox", label: "Inbox", hint: "to review", count: q.inbox, icon: Inbox, go: () => navigate(data.links.inbox) },
    { key: "no_location", label: "No Location", hint: "to fix", count: q.no_location, icon: MapPin, go: () => navigate(data.links.no_location) },
    { key: "needs_tagging", label: "Needs Tagging", hint: "to tag", count: q.needs_tagging, icon: Tags, go: () => setPanel("needs_tagging") },
    { key: "unregistered_tags", label: "Unregistered Tags", hint: "to review", count: q.unregistered_tags, icon: AlertTriangle, go: () => setPanel("unregistered") },
    { key: "tag_integrity", label: "Tag Integrity", hint: "to review", count: q.tag_integrity ?? 0, icon: ShieldCheck, go: () => navigate(data.links.tag_integrity || "/admin?tab=tag-integrity") },
  ];
  const total = cards.reduce((a, c) => a + c.count, 0);
  return (
    <section data-testid="work-queue" className="space-y-6">
      <div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <h2 className="font-heading text-2xl">Needs Attention</h2>
          <span data-testid="work-queue-total" className="text-xs text-muted-foreground">{total === 0 ? "All clear — nothing waiting" : `${total} items waiting`}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {cards.map((c) => (
            <button key={c.key} data-testid={`queue-${c.key}`} data-count={c.count} onClick={c.go}
              className={cn("group text-left rounded-xl border p-4 transition-colors", c.count > 0 ? "border-primary/40 bg-primary/5 hover:bg-primary/10" : "border-border bg-card hover:border-primary/40")}>
              <div className="flex items-center justify-between">
                <c.icon className={cn("w-4 h-4", c.count > 0 ? "text-primary" : "text-muted-foreground")} />
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="mt-3 text-3xl font-heading leading-none" data-testid={`queue-${c.key}-count`}>{c.count}</div>
              <div className="mt-1 text-sm font-medium">{c.label}</div>
              <div className="text-xs text-muted-foreground">{c.count === 0 ? "clear" : c.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="qa-upload" onClick={onUpload} className="rounded-full"><Upload className="w-4 h-4 mr-1.5" /> Submit / Upload</Button>
          <Button data-testid="qa-inbox" variant="outline" className="rounded-full border-border" onClick={() => navigate(data.links.inbox)}><Inbox className="w-4 h-4 mr-1.5" /> Review Inbox</Button>
          <Button data-testid="qa-tag" variant="outline" className="rounded-full border-border" onClick={() => setPanel("needs_tagging")}><Tags className="w-4 h-4 mr-1.5" /> Tag Media</Button>
          <Button data-testid="qa-locations" variant="outline" className="rounded-full border-border" onClick={() => navigate(data.links.no_location)}><MapPin className="w-4 h-4 mr-1.5" /> Set Locations</Button>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-heading text-xl">Recent Uploads</h3>
          <button data-testid="recent-view-all" onClick={() => navigate("/my-uploads")} className="text-xs text-primary hover:underline">All my uploads</button>
        </div>
        <div data-testid="recent-uploads" className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {data.recent.map((m) => {
            const src = m.media_type === "video" ? m.cloud_poster_url : (m.file_ref ? thumbUrl(m.file_ref, 300) : null);
            const flags = [m.needs_inbox && "Inbox", m.location_missing && "No location", m.needs_tags && "No tags"].filter(Boolean);
            return (
              <button key={m.id} data-testid={`recent-${m.id}`} onClick={() => onOpenMedia?.(m.id)} className="group text-left rounded-lg overflow-hidden border border-border bg-card hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-black relative">
                  {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Video className="w-5 h-5" /></div>}
                  <span className="absolute top-1 left-1 rounded bg-black/70 px-1 py-0.5 text-[9px] uppercase font-bold flex items-center gap-0.5">{m.media_type === "video" ? <Video className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}</span>
                </div>
                <div className="p-1.5">
                  <div className="text-[11px] truncate" title={m.title}>{m.title || "Untitled"}</div>
                  <div className="text-[10px] mt-0.5 truncate" data-testid={`recent-flags-${m.id}`}>{flags.length ? <span className="text-amber-400">{flags.join(" · ")}</span> : <span className="text-emerald-400">Ready</span>}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div data-testid="review-progress" className="rounded-xl border border-border bg-card px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
        <span className="text-xs uppercase tracking-widest text-muted-foreground self-center">Content Review</span>
        <span>Inbox remaining: <b data-testid="progress-inbox">{q.inbox}</b></span>
        <span>Locations remaining: <b data-testid="progress-no-location">{q.no_location}</b></span>
        <span>Needs tagging: <b data-testid="progress-needs-tagging">{q.needs_tagging}</b></span>
        <span>Unregistered tags: <b data-testid="progress-unregistered">{q.unregistered_tags}</b></span>
        <span>Tag integrity: <b data-testid="progress-tag-integrity">{q.tag_integrity ?? 0}</b></span>
      </div>

      <Dialog open={panel === "needs_tagging"} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent data-testid="needs-tagging-dialog" className="max-w-5xl bg-card border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Needs Tagging <span className="text-muted-foreground text-base">· {needsTag?.count ?? "…"} without owner tags</span></DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Media with no semantic owner tags yet (system tags like portrait/landscape don't count). Open one and pick tags — nothing is tagged automatically.</p>
          {!needsTag ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            : needsTag.items.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">Everything has owner tags.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">{needsTag.items.map((m) => <MediaTile key={m.id} item={m} onOpen={() => { setPanel(null); onOpenMedia?.(m.id); }} />)}</div>}
        </DialogContent>
      </Dialog>

      <Dialog open={panel === "unregistered"} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent data-testid="unregistered-dialog" className="max-w-lg bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Unregistered Tags <span className="text-muted-foreground text-base">· {data.unregistered.length}</span></DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Owner values on live media that are not canonical tags or aliases. Review only — nothing is changed here. Decide per value: create a canonical tag, add an alias, replace, or remove on the media.</p>
          {data.unregistered.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">All owner tags are registered.</p> : (
            <ul className="divide-y divide-border text-sm">
              {data.unregistered.map((r) => (
                <li key={r.tag} data-testid={`unregistered-${r.tag.replace(/\s+/g, "-")}`} className="flex items-center justify-between py-2">
                  <span className="font-medium">{r.tag}</span>
                  <span className="text-xs text-muted-foreground">{r.count} media · {r.status.split(" — ")[0]}</span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
